/*
 * Resolves what CARFAX returned for a vehicle onto a row in the make/model catalogue.
 *
 * ⚠ Why this exists. Three copies of this cascade had been kept in step by hand - the inventory
 * mixin, ApprovePpaAction and the roServiceVehicle mixin - differing only in `let`/`var` and a blank
 * line. It is the sibling of utils/carfax_make_model_comparison, extracted for the same reason: this
 * half decides WHICH catalogue row the record should land on, that half decides whether the record
 * disagrees with it. The justification is deduplication, not a behaviour change - the two passes
 * below are the existing cascade, moved.
 *
 * ⚠ THE MODEL IS THE FULL NAME, THE GROUP IS `series`. A Ferrari 296 GTS is catalogue row
 * name "296 GTS" with series "296", so "report on all the 296 series" comes from Model.series and
 * needs no schema change. That reading is deliberate and contradicts a literal
 * "model = 296, trim = GTS" split, because the catalogue is not consistent about which way round the
 * two are stored: of 1,816 active rows, 426 carry no series at all and 701 store
 * name = full model / series = group. Encoding one convention would contradict the rest.
 *
 * ⚠ NO FUZZY OR SERIES-JOINED MATCHING, and that is a measured decision rather than a gap. Passes
 * keyed on Model.series were built and then REMOVED after replaying all 399 stored ratings against
 * all 1,816 active catalogue rows: they resolved 286 before and 286 after - zero gained, zero lost.
 * The cases that fail are not shaped differently, they are ABSENT: Porsche 2021 holds 59 rows and
 * not one is a Taycan; Mercedes 2021 holds 2 rows and neither is a C-Class; Porsche 2024 holds a
 * single row and it is not a GT3. 48 of the 107 failures have no row for that make and year at all.
 * That is a catalogue-data problem, and a looser matcher would answer it by picking a wrong row -
 * strictly worse than the free-text fallback, which at least tells the truth. If this is revisited,
 * re-run the replay first; a pass that gains nothing is complexity with a maintenance cost.
 *
 * ⚠ THE REPLAY WAS RE-RUN on 2026-09-08, and BOTH results stand, because they measure different
 * things. The paragraph above is about CARFAX ratings, which carry a real series - so model+series
 * already exact-matches and a series pass genuinely adds nothing there. The VIN DECODE carries no
 * series at all (VehicleLookupModel has no such property), so the bare group name is the only thing
 * it has to match on. Against decode-shaped data - 21,426 active records with no model_id but a
 * Model name - 482 have exactly one catalogue candidate and 844 have between 2 and 19.
 *
 * ⚠ And the rule above is KEPT, not weakened: findCatalogueModelCandidates never picks. It returns
 * the candidates so the operator can choose, which is what the old replay discarded when it counted
 * only unique resolutions. Nothing in this file resolves a group name to a row on its own.
 */

const squash = (value) => String(value === null || value === undefined ? '' : value).replace(/\s+/g, ' ').trim()
const key = (value) => squash(value).toUpperCase()
/*
 * ⚠ The learned-mapping key (findLearnedMapping), one of THREE implementations that must agree with
 * CarfaxModelMapping.NormaliseKey in C# and the seed in db/alter_script.sql. Exported so the shared
 * fixture src/LiveTests/TestData/carfax-normalise-key-cases.json can pin it.
 */
export const carfaxMappingKey = key

/*
 * The same key with the spaces taken out, for EQUALITY between two model names only.
 *
 * The catalogue stores "S 580" and CARFAX sends "S580" - the same vehicle, and squash cannot close
 * the gap because it collapses runs of whitespace rather than removing single spaces. Meeting change
 * #8, stock FP7465.
 *
 * ⚠ NEVER SUBSTITUTE THIS FOR key() WHOLESALE. Four things depend on the space surviving, and each
 * fails silently rather than loudly:
 *   - the name-PREFIX arms at :221 and :276 build `key(name) + ' '`; a trailing space can never occur
 *     in a de-spaced key, so the arm matches nothing. 515 records reach the picker through the name
 *     arm alone and 72 through the series arm.
 *   - the series first-token split at :263 turns "Turbo S" into one token "TURBOS", which drops
 *     "911 Turbo" from the Turbo family - the regression that function exists to prevent.
 *   - modelNameCarriesTrim at :812 splits on spaces to match whole-word runs; de-spaced it always
 *     returns false, and the reconcile prose reads "296 GTS GTS" again. It is now load-bearing for
 *     whether an operator is interrupted at all - see the comment there.
 *   - comparableKey at :502 feeds the did-it-actually-move test at :625 across all five fields, and
 *     the CARFAX overwrite note is the only durable record of the replaced values.
 * The substring search at :331 would also silently widen.
 */
export const tightKey = (value) => key(value).replace(/\s/g, '')

/*
 * The reconcile prompt's "Currently selected" row - meeting change #15.
 *
 * ⚠ IT EXISTS BECAUSE NULL COULD NOT SAY WHICH OF TWO THINGS WAS MEANT. The current row used to
 * carry model: null, and applyCarfaxMakeModel tests `chosenModel && chosenModel.id > 0`, so "the
 * operator ticked nothing" and "the operator ticked keep-mine" arrived identically and both fell
 * through to applying CARFAX's model. Pressing Apply on a row labelled "Currently selected"
 * therefore overwrote the model with CARFAX's - reproduced on Porsche 1075075, modelId 153153 to
 * 102126, which is CARFAX's own row.
 *
 * ⚠ The two beliefs were both written down, in different files, and that is the actual defect.
 * CarfaxModelReconcile said "Apply with no interaction changes no model - the safe default"; the
 * inventory mixin said the same null "applies the 'current' row ... and the selector then falls back
 * to CARFAX's own resolution". Both comments have been corrected to match this marker.
 *
 * ⚠ A MARKER RATHER THAN THE RECORD'S OWN MODEL, because a record already held as free text has no
 * catalogue object to pass - and that is precisely the record most likely to be sitting in front of
 * an operator who means "mine is right, leave it".
 *
 * ⚠ Null still means "nothing ticked" and still falls through to CARFAX. Unreachable from the CARFAX
 * prompt, whose onChoose always selects and never clears, but the decode prompt deliberately
 * pre-ticks nothing and shares the same apply path.
 */
/*
 * The catalogue row's series, for the two surfaces that build their record shape from a flat DTO
 * rather than from the catalogue object - the quick view and the inventory grids. Meeting change #8.
 *
 * ⚠ THE GRID HAS NO SERIES COLUMN and should not grow one. InventoryGridDto is mapped by ORDINAL,
 * and the column it already carries as VehicleSeriesName is `model_series` - a make-prefixed
 * grouping label that falls back to the model name - not this. allVehicleMakes is loaded once at
 * login and both callers already hold it, so the lookup is free.
 */
export const catalogueModelSeries = (allVehicleMakes, makeId, modelId) => {
  if (!allVehicleMakes || !(modelId > 0)) return ''
  const foundMake = allVehicleMakes.find((x) => x.id === makeId)
  const version = foundMake && foundMake.versions ? foundMake.versions.find((x) => x.id === modelId) : null
  return (version && version.series) || ''
}

export const KEEP_CURRENT_MODEL = Object.freeze({ keepCurrentModel: true })

export const isKeepCurrentModel = (model) => !!(model && model.keepCurrentModel === true)

// CARFAX sends 'N/A' where it has no answer, so a bare truthiness test would match on the string.
/*
 * ⚠ 'null' IS A STRING HERE, not a missing value. Measured on dev: 6 ratings carry the four
 * characters n-u-l-l in vehicle_model, and 'Not specified' appears in vehicle_series - both written
 * as text by the extractor. Unrejected they read as a genuine CARFAX value that no record can match,
 * so the operator sees a difference with nothing on the other side of it.
 *
 * ⚠ 'CONVERTIBLE' MUST NEVER JOIN THIS LIST, though CARFAX does put it in the series slot. This
 * predicate is shared by all five fields and Convertible is a real STYLE, pinned across four spec
 * files. It needs no special case anyway - a record named "296 GTB" already matches the bare-model
 * reading of "296 GTB" + "COUPE".
 *
 * ⚠ The server rejects THE SAME SET, in CarFaxService.MakeCarfaxValueSafe. Keep them together;
 * they are two halves of one idea and have now drifted TWICE - first over 'Not specified', then
 * over 'unknown', which sat on the server list alone for long enough that a rating carrying it in
 * vehicle_series read as a real CARFAX series here while the server read the same row as absent.
 */
//Exported, frozen, for the parity spec - it compares THIS list with the server's, not a copy of it.
export const PLACEHOLDER_VALUES = Object.freeze(['', 'n/a', 'null', 'not specified', 'unknown'])

const isMeaningful = (value) => PLACEHOLDER_VALUES.indexOf(squash(value).toLowerCase()) === -1

/*
 * The same test, exported for the callers that apply these values rather than resolve them.
 *
 * ⚠ Both selectors used a bare `value.trim() !== ''` before applying carfax's trim and style, and
 * neither resolveCarfaxMakeModel nor buildCarfaxCustomMakeModel guards STYLE for 'N/A' - only trim.
 * So the literal string CARFAX sends for "no answer" was written onto the vehicle as a real style.
 */
export const isMeaningfulValue = isMeaningful

const applyTo = (version, style, trim) => ({
  resolved: true,
  modelId: version.id,
  selectedModel: version,
  productionYear: version.productionYear,
  style: style,
  trim: trim
})

/*
 * Whether a catalogue row is for this year. ⚠ Number() on both sides, as the candidate finders below do:
 * the catalogue carries the year as a number while years reach here from DTOs and selectors as text too.
 * Strict equality here while the finders normalised meant the candidates found a model this resolver
 * missed - the CARFAX row stayed free text beside a catalogue row of the same name (FP7489A, 2026-09-29).
 * No year still matches nothing.
 */
const isCatalogueYear = (catalogueYear, productionYear) =>
  productionYear !== null && productionYear !== undefined && productionYear !== '' &&
  Number(catalogueYear) === Number(productionYear)

/**
 * The catalogue row named exactly this, for exactly this year, or null.
 *
 * ⚠ Extracted because FOUR inline copies of this one expression had been kept in step by hand -
 * VinNoSelector twice (decodeVin and decodeVinPpa), RoVinNoSelector, and the inventory mixin's
 * processPpaLookupResult - alongside the two passes below. The justification is deduplication: it is
 * the same match those copies performed, moved.
 *
 * ⚠ It deliberately does NOT filter retired models, unlike findCatalogueModelCandidates. Every one
 * of those call sites resolved onto a Deleted row if the name matched, and a record already sitting
 * on a retired model must keep resolving rather than silently falling to free text. The candidate
 * finder is new code and gets the stricter rule; findActiveCatalogueModel below is the variant for
 * callers that link a model.
 *
 * ⚠ The Number() year (isCatalogueYear) changed nothing for those four: each converts the decode's year
 * with unary + before calling here, so they only ever pass a number (or NaN/0, which match nothing either
 * way). It is the CARFAX paths, which pass the year as it arrived, that a text year used to miss.
 */
export const findCatalogueModel = (foundMake, modelName, productionYear) => {
  if (!foundMake || !foundMake.versions || foundMake.versions.length === 0) return null
  if (!isMeaningful(modelName)) return null

  //⚠ tightKey, so the catalogue's "S 580" answers to CARFAX's "S580" - see its note above.
  const modelKey = tightKey(modelName)
  return foundMake.versions.find((x) => isCatalogueYear(x.productionYear, productionYear) && tightKey(x.name) === modelKey) || null
}

/*
 * findCatalogueModel without retired rows - for callers that LINK a model rather than recognise one.
 * Filtering before the search, not after, so a retired duplicate cannot mask the active row of the
 * same name. findCatalogueModel itself keeps returning retired rows for its existing callers.
 */
export const findActiveCatalogueModel = (foundMake, modelName, productionYear) => {
  if (!foundMake || !foundMake.versions) return null
  const versions = foundMake.versions.filter((x) => x.status !== 'Deleted')
  return findCatalogueModel(Object.assign({}, foundMake, { versions }), modelName, productionYear)
}

/**
 * @param foundMake     the make matched out of allVehicleMakes (the caller has already resolved it)
 * @param carfaxValues  { model, series, style, productionYear } straight off the carfax rating
 * @returns { resolved: false } when nothing matched - the caller then falls back to free text - or
 *          { resolved: true, modelId, selectedModel, productionYear, style, trim }
 */
export const resolveCarfaxMakeModel = (foundMake, carfaxValues) => {
  const values = carfaxValues || {}
  const model = values.model
  const series = values.series
  const style = values.style
  const productionYear = values.productionYear

  const unresolved = { resolved: false }

  if (!foundMake || !foundMake.versions || foundMake.versions.length === 0) return unresolved
  if (!isMeaningful(model)) return unresolved

  const modelKey = key(model)
  const hasSeries = isMeaningful(series)
  const modelSeriesKey = hasSeries ? key(`${model} ${series}`) : modelKey

  /*
   * The row is named for the whole thing - "296 GTS" - which is how most of the catalogue stores it.
   *
   * ⚠ tightKey on BOTH sides. The join above inserts a space between the halves, so CARFAX's "S" plus
   * "580" builds "S 580" while the catalogue may hold either "S580" or "S 580". Comparing space-blind
   * is what lets the pair land on its row either way.
   */
  const foundModelSeries = foundMake.versions.find(
    (x) => isCatalogueYear(x.productionYear, productionYear) && tightKey(x.name) === tightKey(modelSeriesKey)
  )
  if (foundModelSeries) {
    /*
     * ⚠ NO TRIM, EVER, ON THIS ARM. The row matched here is by construction the one NAMED for the
     * model and the series together - "296 GTS" - so carrying "GTS" as the trim as well is the same
     * redundancy composeVehicleDescription and CarFaxService.RemoveRedundantSeriesValue already strip
     * back out downstream, and it is what made the prompt read "296 GTS GTS".
     *
     * It also closes a provenance hole. applyMakeModelCascade writes this trim onto the record
     * whenever it is meaningful, independent of any difference flag, while buildCarfaxOverwriteNote
     * names only the fields the caller FLAGGED - and the note is the only durable record of what the
     * correction replaced. With the trim blank there is nothing to overwrite, so a record's real trim
     * cannot be replaced by a value the note never mentions.
     *
     * The sibling shape below - the extraction that already folded the sub-model in and left the
     * series blank - has always returned '' here, so this makes the two agree.
     */
    return applyTo(foundModelSeries, style, '')
  }

  // The row is named for the model alone, and the sub-model stands as the trim.
  const foundModel = findCatalogueModel(foundMake, model, productionYear)
  if (foundModel) return applyTo(foundModel, style, series)

  return unresolved
}

/*
 * The free-text half of the resolution - what CARFAX said, when the catalogue holds no row for it.
 *
 * ⚠ Returns the fields to MERGE, rather than mutating a carfaxMakeModel in place, because the three
 * hosts each had a byte-identical `buildCarfaxCustomMakeModel` doing the mutation and a fourth was
 * about to be written for the quick-view. Their methods now delegate here, so there is one definition
 * and their own tests keep passing against it.
 *
 * ⚠ Only present values are returned, exactly as the mutating version only assigned inside its `if`s -
 * so a field CARFAX has no answer for leaves whatever the caller already held. The 'n/a' guard is on
 * the trim alone, which is the pre-existing behaviour and NOT an oversight to tidy: the style guard
 * lives at the apply site instead (MakeModelSelector.applyMakeModelCascade).
 */
export const buildCarfaxCustomMakeModelValues = (make, productionYear, model, trim, style) => {
  const values = {}
  let description = ''

  if (productionYear) {
    description = productionYear
    values.productionYear = productionYear
  }
  if (make) {
    description += ' ' + make
    values.customMakeName = make
  }
  /*
   * ⚠ isMeaningful, not truthiness, and this is the branch a rejected model FALLS INTO - so without
   * it the reject list changes nothing an operator can see. A model of 'null' fails to resolve,
   * arrives here, is truthy, and is written to customModelName, which is what the reconcile picker
   * and the comparison table then print back as a row named "null".
   */
  if (isMeaningful(model)) {
    description += ' ' + model
    values.customModelName = model
  }
  if (isMeaningful(trim)) {
    description += ' ' + trim
    values.trim = trim
  }
  if (isMeaningful(style)) {
    description += ' ' + style
    values.style = style
  }

  values.customMakeModel = description
  return values
}

/**
 * Every catalogue row that could be the model, when what we were given is a model GROUP.
 *
 * ⚠ Offers, never picks. A group name is genuinely ambiguous - Ferrari 296 in 2024 is two rows
 * (296 GTB, 296 GTS) and Alfa Giulia in 2020 is thirteen - so the caller shows these and the
 * operator chooses. Auto-selecting would be wrong 12 times in 13 on the Alfa shape.
 *
 * Two arms, because the catalogue is not consistent about where the group is stored: Ferrari puts
 * it in `series` on each variant row, while most Porsche rows leave `series` blank and carry it as
 * a name prefix. Both are matched through the shared key(), which trims - one real Alfa row stores
 * its series as "Giulia " with a trailing space.
 *
 * @param foundMake       the make already resolved out of allVehicleMakes
 * @param modelName       the model name the decode or the record is carrying
 * @param productionYear  numeric year; rows from other years are never candidates
 * @returns [] when there is nothing to disambiguate, otherwise the candidate rows
 */
export const findCatalogueModelCandidates = (foundMake, modelName, productionYear) => {
  const none = []

  if (!foundMake || !foundMake.versions || foundMake.versions.length === 0) return none
  if (!isMeaningful(modelName)) return none
  if (productionYear === null || productionYear === undefined || productionYear === '') return none

  /*
   * ⚠ The catalogue carries productionYear as a NUMBER, while both selectors hold selectedYear as
   * a STRING on load - it arrives from the DTO as text and only becomes a number if the operator
   * re-picks it from the dropdown. A strict comparison therefore matched nothing and this function
   * returned empty for every record it exists to serve, silently. Normalised here rather than at the
   * two call sites so a third caller cannot reintroduce it.
   */
  const year = Number(productionYear)
  if (!Number.isFinite(year)) return none

  const modelKey = key(modelName)

  // ⚠ Retired models must not come back through this door - 9f98685d5 stopped them reaching the
  // pickers, and a candidate list that ignored status would quietly undo it.
  const forYear = foundMake.versions.filter(
    (x) => Number(x.productionYear) === year && x.status !== 'Deleted'
  )

  /*
   * ⚠ The guard that keeps this behaviour-neutral. When the catalogue holds the name outright there
   * is nothing to disambiguate: the existing resolver lands on that row and the record is not in the
   * population this exists for. It also makes the function safe to call unconditionally.
   *
   * ⚠ tightKey, and it MUST match findCatalogueModel's equality exactly. This guard mirrors the
   * resolver, so the moment the two normalise differently they disagree about whether a row exists:
   * a looser guard offers a picker for a record the resolver already placed, and a tighter one
   * withholds the picker from a record the resolver leaves unresolved. The prefix below keeps the
   * loose key, because a de-spaced prefix can never match.
   */
  if (forYear.some((x) => tightKey(x.name) === tightKey(modelName))) return none

  const groupPrefix = modelKey + ' '

  // One filter, so a row selected by both arms - "911 Speedster" carries series "911" too - is
  // returned once rather than needing a dedupe pass.
  return forYear.filter((x) => key(x.series) === modelKey || key(x.name).indexOf(groupPrefix) === 0)
}

/*
 * The catalogue models in the same GROUP as what CARFAX reported, narrowed to those sharing its
 * series.
 *
 * ⚠ Keyed on CARFAX's RAW model and series, not on whatever the resolver landed on. That is the
 * whole point. CARFAX sends the two separately - model "911", series "Turbo S" - and the resolver
 * joins them, so by the time the modal runs, the only name available is "911 Turbo S". Searching on
 * that can only reach rows BELOW it ("911 Turbo S Cabriolet"), never its siblings. A 2021 Porsche
 * that resolved to "911 Turbo S" therefore could not offer "911 Turbo", which exists for that year.
 *
 * ⚠ NO exact-name guard, deliberately - this is the one difference from findCatalogueModelCandidates
 * above. That function returns nothing the moment the name it was given exists outright, because
 * there is then nothing to disambiguate. Here the opposite is true: the siblings are wanted
 * PRECISELY because the joined name resolved. Re-adding that guard would silently restore the defect,
 * and it would fire on almost every call, since the bare group name ("911") is usually a real row.
 *
 * ⚠ Narrowed by the series token rather than offering the bare group. The 2021 Porsche 911 group is
 * 25 rows - Carrera, Targa, GT3, Speedster - which buries the answer. Taking the FIRST token of the
 * series ("Turbo S" -> "TURBO") keeps 911 Turbo, 911 Turbo Cabriolet and the Turbo S variants and
 * drops the rest.
 *
 * Returns [] whenever it cannot run, so the caller falls back to the original finder and nothing
 * that works today changes.
 */
export const findCatalogueModelGroupCandidates = (foundMake, groupName, seriesName, productionYear) => {
  const none = []

  if (!foundMake || !foundMake.versions || foundMake.versions.length === 0) return none
  if (!isMeaningful(groupName) || !isMeaningful(seriesName)) return none
  if (productionYear === null || productionYear === undefined || productionYear === '') return none

  //Same coercion as findCatalogueModelCandidates, and for the same reason - see its note. The
  //catalogue holds a number while the rating carries a string.
  const year = Number(productionYear)
  if (!Number.isFinite(year)) return none

  //⚠ The FIRST token only. A whole series of "Turbo S" would exclude "911 Turbo", which is the row
  //this exists to surface. One character is too weak a filter to narrow anything, so bail and let
  //the caller fall back rather than return the entire group.
  const seriesToken = key(seriesName).split(' ')[0]
  if (seriesToken.length < 2) return none

  const groupKey = key(groupName)
  const groupPrefix = groupKey + ' '

  // ⚠ Retired models must not come back through this door either - same reasoning as above.
  const forYear = foundMake.versions.filter(
    (x) => Number(x.productionYear) === year && x.status !== 'Deleted'
  )

  return forYear.filter(
    (x) =>
      (key(x.name).indexOf(groupPrefix) === 0 || key(x.series) === groupKey) &&
      key(x.name).indexOf(seriesToken) !== -1
  )
}

/*
 * The catalogue models whose name CONTAINS what the operator typed.
 *
 * ⚠ A SECOND finder, deliberately, and not a loosening of findCatalogueModelCandidates above. That one
 * matches on series or a "<group> " PREFIX and is what the VIN-decode and CARFAX prompts run on;
 * widening it to a substring would silently change three live prompts. This one serves the typed path
 * only, where the operator may type any fragment - "Superfast" should reach "812 Superfast" just as
 * "812" does, and a prefix rule cannot do that.
 *
 * ⚠ Reads foundMake.versions, NOT the selector's `models` computed. That computed concatenates
 * localModels, which already holds free-text rows from earlier typing, decodes and CARFAX applies -
 * searching it would offer the operator their own previous mistakes back as catalogue candidates.
 *
 * ⚠ MINIMUM LENGTH, because this runs on whatever was typed. One or two characters match most of a
 * 120-model make, which is a prompt nobody can act on. The search only ever runs when the operator
 * commits or leaves the field, never per keystroke, so the floor can stay low.
 *
 * @param foundMake      the catalogue make, with versions
 * @param text           what the operator typed
 * @param productionYear the year selected on the record
 * @returns the matching catalogue rows, or [] when there is nothing worth asking about
 */
export const MINIMUM_CONTAINS_SEARCH_LENGTH = 3

export const findCatalogueModelsContaining = (foundMake, text, productionYear) => {
  const none = []

  if (!foundMake || !foundMake.versions || foundMake.versions.length === 0) return none
  if (!isMeaningful(text)) return none

  const needle = key(text)
  if (needle.length < MINIMUM_CONTAINS_SEARCH_LENGTH) return none

  //⚠ Same year normalisation as findCatalogueModelCandidates, and for the same reason recorded there:
  //the catalogue carries productionYear as a number while the selectors hold it as a string on load.
  const year = Number(productionYear)
  if (!Number.isFinite(year)) return none

  const forYear = foundMake.versions.filter(
    (x) => Number(x.productionYear) === year && x.status !== 'Deleted'
  )

  /*
   * ⚠ The same behaviour-neutral guard the group finder uses. When the catalogue holds the typed name
   * outright there is nothing to disambiguate - and on this path it also means the operator could have
   * picked it from the list, so asking would be nonsense.
   */
  if (forYear.some((x) => key(x.name) === needle)) return none

  //⚠ A row whose name IS the needle is excluded by the guard above; this cannot echo the input back.
  return forYear.filter((x) => key(x.name).indexOf(needle) !== -1)
}

/**
 * Whether a decoded model name is a GROUP the catalogue can disambiguate, and so must be put to the
 * operator instead of written onto the record.
 *
 * ⚠ It takes the make LIST and a make NAME, not a resolved make, because the two callers are the VIN
 * decode's free-text fallback in VinNoSelector and its copy in the inventory mixin - and both reach
 * that fallback by three routes, one of which is "the make did not resolve". Resolving in here keeps
 * all six call sites honest: an unresolved make has no models behind it and returns false.
 *
 * ⚠ Shared rather than inlined in both, deliberately. The decode decides whether to write and the
 * selector decides whether to prompt; if those two answers ever disagree the record ends up with no
 * model and no prompt, which is worse than the bug being fixed. One predicate is what keeps them
 * agreeing.
 *
 * @param makes           the catalogue makes (allVehicleMakes)
 * @param makeName        the make name the decode returned
 * @param modelName       the model name the decode returned
 * @param productionYear  the decoded year; string or number, normalised downstream
 */
export const decodedModelNeedsChoice = (makes, makeName, modelName, productionYear) => {
  if (!makeName || !makes || makes.length === 0) return false
  const wanted = String(makeName).toUpperCase()
  const foundMake = makes.find((x) => x && x.name && String(x.name).toUpperCase() === wanted)
  return findCatalogueModelCandidates(foundMake, modelName, productionYear).length > 0
}

/**
 * The one-line pointer above the comparison table in the reconcile prompt.
 *
 * ⚠ It deliberately quotes NOTHING. The modal used to render this sentence as its entire body, with
 * both vehicle descriptions inlined - "...returned from CARFAX, 2023 Chevrolet Corvette Stingray
 * Convertible, differs from the current vehicle information, 2023 Chevrolet Corvette 2LT 2D
 * Convertible..." - so the operator had to diff two prose strings to find the single field that moved.
 * Garrett, on PPA108227: "I can't even understand this, it's almost too much blah blah blah."
 * (31 Aug, change 5.) The values now live in CarfaxVehicleComparison; this only points at them.
 *
 * ⚠ This SUPERSEDES the row-naming branch that used to live here. That branch existed because the two
 * descriptions could render identically and the sentence would then say "X differs from X" - a real
 * defect, fixed earlier the same day. Once the sentence stopped quoting descriptions the collision
 * could not arise at all, so the branch and its tests were removed rather than left as dead weight.
 * The table shows the rows, which is a better answer than naming them in prose.
 */
/*
 * ⚠ canCorrect drops the closing QUESTION, and defaults to asking it, so every existing caller is
 * unchanged. The quick view passes the user's inventory.modify right: without it the Edit button is
 * hidden, and a banner asking "Would you like to update...?" above a dialog whose only button is Close
 * asks something the reader cannot answer.
 */
export const buildReconcileMessage = (canCorrect = true) => {
  const statement = 'The CARFAX report returned different vehicle information for this record. Compare the existing value to that of the CARFAX report below.'
  return canCorrect ? statement + ' Would you like to update the vehicles information to match the CARFAX record?' : statement
}

/*
 * A pop-fill chip's tooltip, naming BOTH the field it fills and where the value came from.
 *
 * ⚠ THE SOURCE IS AN ARGUMENT, not a flag read from somewhere. The chip's whole job is to tell the
 * operator what warrant a value has - "the CARFAX report" is a paid vehicle-history record, "vindecode"
 * is a catalogue lookup, and an operator deciding whether to trust an unrecognised trim needs to know
 * which. Holding it as component state made it a one-way latch: whichever path last happened to set it
 * spoke for every chip armed afterwards.
 *
 * ⚠ SHARED because MakeModelSelector and RoMakeModelSelector are a ~94% fork and every RO defect on
 * this workstream traces to one being updated and the other not - see the header of
 * roServiceVehicleCarfaxButtons.spec.js. The RO copy carried this sentence seven times as a hardcoded
 * literal saying "vindecode", on chips its CARFAX path arms.
 */
export const buildOfferedValueTooltip = (fieldName, source) =>
  'Click to use this value as the ' + fieldName + '. This is a value from ' + source +
  ' that we do not have in our system'

/*
 * The model-choice prompt's sentence, which names the single field that moved.
 *
 * ⚠ A SECOND builder rather than an argument on the one above, and deliberately so. The other
 * six surfaces still render the yes/no prompt and its generic sentence; overloading one function
 * silently turned their text into "a different model, not set than the one currently selected not
 * set". They are separate messages for separate prompts until every surface has moved, at which
 * point buildReconcileMessage above loses its last caller and goes.
 *
 * ⚠ Names the MODEL only. What was unreadable before was two full vehicle descriptions inlined
 * in one sentence; naming the one field that differs is the opposite of that, and the table below
 * it still carries the values.
 */
export const buildModelReconcileMessage = (carfaxModelName, currentModelName) =>
  'The CARFAX report returned a different model, ' + shownOrNotSet(carfaxModelName) +
  ' than the one currently selected ' + shownOrNotSet(currentModelName) +
  '. Would you like to apply the CARFAX information? Select the model to apply below.'

/*
 * The sentence for the other half of the reconcile action: the model agrees, so the model choice does
 * not arise, but trim, style or year on the report do not match and can still be taken.
 *
 * ⚠ A THIRD builder, not an argument on either of the two above. The reconcile prompt asks a
 * question and this one answers a different one; the last time these were merged, overloading
 * buildReconcileMessage silently blanked five live prompts to "a different model, not set than the
 * one currently selected not set".
 *
 * ⚠ It exists because the match icon reports all five fields while the model prompt reconciles only
 * the model. That split is deliberate, and it means the operator can arrive here with the model
 * already settled and the remaining fields still worth taking - so this asks, rather than reporting.
 */
export const buildFieldDifferencesMessage = (modelName) =>
  'The model ' + shownOrNotSet(modelName) + ' already matches the CARFAX report, would you like to ' +
  'reconcile the other information?'

/*
 * The VIN decode's own prompt. The decode returns a model GROUP - "812", "911", "296", "Giulia" -
 * where the catalogue holds the specific models, so this asks which one the vehicle actually is.
 *
 * ⚠ A FOURTH builder, and for the same reason as the third. There is no "currently selected" model to
 * name here: the decode no longer writes one when candidates exist, which is the entire change. Routing
 * this through buildModelReconcileMessage would append "than the one currently selected not set" to
 * every decode prompt - the exact failure that blanked five live prompts when buildReconcileMessage
 * was overloaded.
 *
 * ⚠ It says nothing about CARFAX. This prompt can raise before any CARFAX report exists - the decode
 * runs first - and naming a report the operator has not seen reads as a fault in the report.
 */
export const buildDecodedModelChoiceMessage = (decodedModelName) =>
  'The VIN decode returned ' + shownOrNotSet(decodedModelName) + ', which is a model group rather ' +
  'than a model. Select the model this vehicle actually is below.'

/*
 * The typed path's message.
 *
 * ⚠ A FIFTH builder rather than a flag on one of the others, for the reason this file keeps
 * repeating: overloading buildReconcileMessage once blanked five live prompts. The wording also has a
 * job the others do not - the operator typed this value deliberately, so the sentence must not read as
 * a correction. Garrett, 17 Sep: "Are you sure it's not one of these?"
 */
export const buildTypedModelChoiceMessage = (typedModelName) =>
  shownOrNotSet(typedModelName) + ' is not in the vehicle make/model data. These models contain it - ' +
  'select one, or keep what you entered.'

/*
 * The two halves of the warning shown when the row the operator has TICKED is not a catalogue model.
 *
 * ⚠ Why words at all. The reconcile table already renders such a row red, and that red has been the
 * entire signal - no icon, no tooltip, no legend. "Red means this saves as free text and drops the
 * record out of model reporting" was knowledge the operator had to already hold. It is not a rare
 * edge either: 35 of the 119 stored ratings that name a model report one the catalogue does not
 * contain.
 *
 * ⚠ TWO builders because the two cases are not the same sentence, and sharing one would make it lie.
 * An offered row is something the operator is about to APPLY, so the cost is worth stating. The
 * record's CURRENT row applies nothing - its model is null - so promising it "will save free text"
 * would describe an action that never happens. A flag on one builder is what this file keeps
 * refusing, for the reason recorded above buildDecodedModelChoiceMessage.
 */
export const buildNotInCatalogueWarning = (modelName) =>
  shownOrNotSet(modelName) + ' is not in the vehicle make/model data. Applying it will save the ' +
  'model as free text, with no catalogue link.'

//⚠ Lower-case "this record" deliberately: CarfaxModelReconcile.spec.js asserts the rendered html
//never contains "This record", which pins the field-by-field comparison table out of this component.
export const buildCurrentNotInCatalogueWarning = (modelName) =>
  'The model on this record, ' + shownOrNotSet(modelName) + ', is not in the vehicle make/model data.'

/*
 * The five fields the reconcile table shows, in the order it shows them, keyed by the flags the
 * comparison already produced. One list, so the note and the table can never name different things.
 */
const OVERWRITTEN_FIELDS = [
  { key: 'productionYear', label: 'Year' },
  { key: 'make', label: 'Make' },
  { key: 'model', label: 'Model' },
  { key: 'trim', label: 'Trim' },
  { key: 'style', label: 'Style' }
]

const shownOrNotSet = (value) => (isMeaningful(value) ? squash(value) : 'not set')

//⚠ Collapses every flavour of "absent" to one value, so N/A, '' and null compare equal. A field moving
//between them has not changed in any sense the operator cares about. Also normalises type, because the
//record's year arrives as a string attribute while the catalogue's is a number.
const comparableKey = (value) => (isMeaningful(value) ? key(value) : '')

/**
 * The five vehicle fields as flat values, read off either side's make/model object.
 *
 * ⚠ The catalogue row and the free-text fallback are the same field to a reader: when carfax resolved
 * no row the values sit on customMakeName / customModelName instead - and that is the case that needs
 * recording most. Defined once so the note and the comparison table cannot come to disagree about
 * which property a name is read from.
 *
 * ⚠ Returns COPIES. `selectedMakeModel` is the selector's own object, shared with the host by v-model
 * and mutated in place by applyCarfaxMakeModel, so anything holding a reference to it is overwritten
 * along with it.
 */
export const readMakeModelFields = (source) => {
  const values = source || {}
  const make = values.selectedMake && values.selectedMake.name
  const model = values.selectedModel && values.selectedModel.name
  return {
    productionYear: values.productionYear,
    make: make || values.customMakeName,
    model: model || values.customModelName,
    trim: values.trim,
    style: values.style,
    /*
     * ⚠ A SIXTH KEY, and deliberately not one of the five. The catalogue splits the vehicle across
     * name and series - 1,507 of 1,953 rows carry a series - and this reader used to drop it, so a
     * record sitting on exactly the right row had nothing but its family name to be compared with.
     *
     * ⚠ NOT added to OVERWRITTEN_FIELDS. That list names the five fields the overwrite note reports
     * and round-trips through parseCarfaxOverwriteNote; series is not a field the operator set and
     * has no place in that sentence.
     */
    series: values.selectedModel && values.selectedModel.series
  }
}

//The sentence the builder always emits, and the anchor the parser reads back.
const CARFAX_NOTE_PREFIX = 'CARFAX vehicle information applied.'

//The label opening the note, naming the vehicle as it stood before CARFAX replaced anything.
const INITIAL_DECODE_LABEL = 'Initial vin-decode'

/**
 * The vehicle described as "<year> <make> <model>", from the flat fields above.
 *
 * ⚠ Deliberately the SAME three parts, in the same order, as InventoryModel.EntityToModel composes
 * Inventory.Name - so the name this composes on a record that has no server-side Name yet cannot drift
 * from the one the server would have produced. Absent parts are skipped rather than rendered blank, so
 * a half-filled record reads "2024 Ferrari" instead of "2024 Ferrari  ".
 *
 * ⚠ Trim and style are NOT included, again because Inventory.Name does not include them.
 */
export const composeVehicleName = (fields) => {
  const values = fields || {}
  return [values.productionYear, values.make, values.model]
    .filter((part) => isMeaningful(part))
    .map((part) => squash(part))
    .join(' ')
}

/*
 * The vehicle as it now reads, in full - year, make, model, trim, style.
 *
 * ⚠ Deliberately WIDER than composeVehicleName, which stops at year+make+model because it mirrors
 * InventoryModel's own Name. This one closes the overwrite note, where the point is what the record
 * BECAME, so the trim and style the apply just wrote have to be in it.
 *
 * ⚠ Same composition updateCustomMakeModel writes onto the form, so the note's closing name and the
 * field the operator is looking at agree - including the two suppressions: 'Base' is dropped, and a
 * trim the model name already carries is not repeated. 701 of 1,816 active catalogue rows store
 * name = full model, so without that second rule a Ferrari note would close "296 GTS GTS".
 */
export const composeVehicleDescription = (fields) => {
  const values = fields || {}
  const model = isMeaningful(values.model) ? squash(values.model) : ''
  const parts = [values.productionYear, values.make, model]

  const isBase = (value) => squash(value).toLowerCase() === 'base'
  if (isMeaningful(values.trim) && !isBase(values.trim) && !modelNameCarriesTrim(model, values.trim)) {
    parts.push(values.trim)
  }
  if (isMeaningful(values.style) && !isBase(values.style)) {
    parts.push(values.style)
  }

  return parts
    .filter((part) => isMeaningful(part))
    .map((part) => squash(part))
    .join(' ')
}

/**
 * The inventory note recording what the operator's Yes replaced.
 *
 * ⚠ Why this exists at all. Nothing else records it. applyCarfaxMakeModel opens by nulling year,
 * model, trim and style; the comparison table is bound to the very object being mutated, so its
 * "This record" column is not a copy; the save rewrites the existing InventoryAttribute row in place
 * and that row has no previous-value column; and AuditTrailListener has @event.OldState in hand and
 * never reads it. So without this the old values are unrecoverable, including from the database.
 * Garrett, having just accepted the change on screen: "This is the data that was there. How do I
 * remember what that was?" (31 Aug, change 5.)
 *
 * ⚠ Names only what the CALLER flagged, never a re-comparison of the two sides. The roService screen
 * deliberately raises make, year and model only, so re-deriving equality here would name rows that
 * screen does not treat as differences - the same rule CarfaxVehicleComparison follows.
 *
 * ⚠ Returns '' when nothing MEANINGFUL was replaced, so a brand-new record whose empty fields CARFAX
 * simply filled in collects no note. There is nothing to remember about a value that was never there.
 *
 * ⚠ It OPENS by naming the vehicle, because on the note timeline this sits among every other note on
 * the record and a run of parenthesised fragments has nothing anchoring it to a car.
 *
 * @param before        { productionYear, make, model, trim, style } - flat strings copied BEFORE the
 *                      apply. A reference to selectedMakeModel would be overwritten along with it.
 * @param after         the same shape, as applied
 * @param differences   { make, productionYear, model, trim, style } booleans
 * @param vehicleName   Inventory.Name off the loaded record, i.e. currentInventory.name. Optional:
 *                      NewInventory has no currentInventory at all - it holds modifiedInventoryItem,
 *                      and only once the record has an id - so on the CREATE path there is no
 *                      server-side name to read and this arrives blank.
 */
export const buildCarfaxOverwriteNote = (before, after, differences, vehicleName) => {
  const previous = before || {}
  const applied = after || {}
  const flags = differences || {}

  //⚠ Flagged AND actually moved. The flags alone are not enough: when carfax resolves no catalogue row
  //applyCarfaxMakeModel falls back to setMakeModelFromCarfaxData, which only OFFERS the values as
  //pop-fills and writes nothing - so the flags stay set while the record is untouched, and a
  //flags-only note would claim "Model (296) updated to (296)". This is not the re-comparison the
  //roService screen must not have: that would be record-vs-carfax, this is record-vs-itself, which is
  //the very thing the note asserts.
  const changed = OVERWRITTEN_FIELDS.filter((field) => !!flags[field.key] && comparableKey(previous[field.key]) !== comparableKey(applied[field.key]))
  if (changed.length === 0) return ''
  if (!changed.some((field) => isMeaningful(previous[field.key]))) return ''

  //Wording follows the house precedent in InventoryService.SetVehicleMakeModelForCustomVehicle -
  //"custom make model ({old}) updated to make model version ({new})." - old value in parentheses.
  //No user name: CreatedBy is stamped on insert by AuditTrailListener and the note timeline already
  //renders "createdAt - createdBy" above the text.
  //⚠ No terminator here - the join supplies it. The LAST sentence ends with a colon when a closing
  //description follows it and a full stop when there is none, so the punctuation cannot be decided
  //field by field.
  const sentences = changed.map(
    (field) => `${field.label} (${shownOrNotSet(previous[field.key])}) updated to (${shownOrNotSet(applied[field.key])})`
  )

  //⚠ Composed from BEFORE, not from the record as it stands, so the name is the initial decode by
  //construction rather than by timing. It is the fallback for the create path, where no saved record
  //exists to have a Name at all.
  //
  //⚠ Omitted entirely when neither source yields anything, rather than emitting "Initial vin-decode ."
  //- the note must not claim a decode it cannot name, and an un-prefixed note still parses.
  const name = isMeaningful(vehicleName) ? squash(vehicleName) : composeVehicleName(previous)
  const preamble = name === '' ? '' : `${INITIAL_DECODE_LABEL}: ${name}. `

  //⚠ Closed with the vehicle as it now reads, after a semicolon. The sentences say what moved field by
  //field, which leaves the reader to reassemble the result in their head - and the record's name is
  //the thing a note read weeks later is actually being matched against. Composed from AFTER, so it is
  //the vehicle the apply produced rather than the one it replaced.
  //
  //⚠ It sits OUTSIDE the sentence grammar on purpose. OVERWRITTEN_SENTENCE is anchored on the five
  //known labels followed by "(x) updated to (y)", so a trailing name cannot be mistaken for one, and
  //parseCarfaxOverwriteNote keeps round-tripping unchanged.
  //
  //⚠ THE COLON IS PART OF THE SENTENCE GRAMMAR, not decoration. Each sentence's terminator is what
  //OVERWRITTEN_SENTENCE matches on, so introducing ":" here without widening that regex to `[.:]`
  //would leave the LAST field unparseable and quietly drop it from the Vehicle Configuration History
  //box and from anything restoring off it. The two must be changed together.
  const updatedDescription = composeVehicleDescription(applied)
  const body = sentences.join('. ')
  const closing = updatedDescription === '' ? '.' : `: ${updatedDescription}.`

  return `${preamble}${CARFAX_NOTE_PREFIX} ${body}${closing}`
}

//`Model (296) updated to (296 GTS).` - anchored on the known labels rather than a generic word match,
//so nothing but a sentence this builder wrote can satisfy it. [^)]* keeps each half from running past
//its own closing bracket.
//
//⚠ [.:] because the LAST sentence ends with a colon when the note closes with the updated vehicle -
//"Style (Coupe) updated to (CONVERTIBLE): 1988 Mercedes-Benz 560-CLASS 560 SL CONVERTIBLE." Requiring
//a full stop would silently drop that final field from every note written since, and a note is only
//as good as what can be read back out of it. It does NOT loosen the guard against parsing an operator
//note: that is NOTE_OPENING's ^-anchor, which is untouched.
const OVERWRITTEN_SENTENCE = new RegExp(
  `(${OVERWRITTEN_FIELDS.map((field) => field.label).join('|')}) \\(([^)]*)\\) updated to \\(([^)]*)\\)[.:]`,
  'g'
)

/*
 * How one of our notes is allowed to OPEN.
 *
 * ⚠ Still ^-anchored, and that is the whole point. This used to be `indexOf(prefix) !== 0` - the note
 * had to START with the CARFAX sentence - and the obvious way to make room for the new preamble is to
 * loosen it to a contains-test. That would be wrong: an operator note quoting a field sentence
 * mid-text would start parsing, and the restore button would act on a human note. The guard test
 * `is null when a field sentence is quoted inside an operator note` exists for exactly that and must
 * keep passing untouched.
 *
 * ⚠ The optional group is also what keeps notes written BEFORE the preamble shipped readable, so
 * existing records keep their history box and their restore. Non-greedy, so the first
 * ". CARFAX vehicle information applied." wins even if a vehicle name ever contains a full stop.
 */
const NOTE_OPENING = new RegExp(
  `^(?:${INITIAL_DECODE_LABEL}: .*?\\. )?${CARFAX_NOTE_PREFIX.replace(/\./g, '\\.')}`
)

const LABEL_TO_KEY = OVERWRITTEN_FIELDS.reduce((map, field) => {
  map[field.label] = field.key
  return map
}, {})

//The inverse of shownOrNotSet.
const parsedValue = (shown) => (squash(shown) === 'not set' ? '' : squash(shown))

/**
 * Reads back the values a CARFAX overwrite note recorded.
 *
 * ⚠ Parsing prose is normally brittle, and it is only defensible here because BOTH ENDS ARE OURS:
 * this is the exact inverse of buildCarfaxOverwriteNote above, pinned by a round-trip test. The
 * builder cannot change shape without that test going red. If a third party ever writes this note,
 * stop parsing and persist the values properly.
 *
 * ⚠ The note is the ONLY durable record of what the record held before CARFAX, which is why it is
 * worth reading back at all. There is no VIN-decode note anywhere - none of the 43,784 inventory
 * records has one - VinHistoryEvent carries only vin/usage/event_type, custom_make_model is blanked
 * the moment a vehicle reconciles to a catalogue row, the five attributes are rewritten in place with
 * no previous-value column, and AuditTrailListener holds OldState and never reads it.
 *
 * @returns null when the text is not one of ours, otherwise
 *          { previous, applied } - each holding ONLY the fields the note names, since a field it does
 *          not name did not move and the record's current value for it is already correct.
 */
export const parseCarfaxOverwriteNote = (noteText) => {
  const text = squash(noteText)
  if (!NOTE_OPENING.test(text)) return null

  const previous = {}
  const applied = {}
  let named = 0

  OVERWRITTEN_SENTENCE.lastIndex = 0
  let match = OVERWRITTEN_SENTENCE.exec(text)
  while (match !== null) {
    const field = LABEL_TO_KEY[match[1]]
    previous[field] = parsedValue(match[2])
    applied[field] = parsedValue(match[3])
    named++
    match = OVERWRITTEN_SENTENCE.exec(text)
  }

  //A prefix with no readable sentence is not something we can restore from, so it is not ours either.
  if (named === 0) return null

  return { previous: previous, applied: applied }
}

/**
 * The oldest CARFAX overwrite note on a record, parsed.
 *
 * ⚠ OLDEST, not newest. Each note records what the value was immediately before that particular
 * apply, so on a record reconciled more than once the earliest note holds the values closest to the
 * original decode - which is what "restore the initial vehicle information" means.
 */
export const findCarfaxOverwriteNote = (notes) => {
  const candidates = (notes || [])
    .map((note, index) => ({ index: index, note: note, parsed: parseCarfaxOverwriteNote(note && note.note) }))
    .filter((candidate) => candidate.parsed !== null)

  if (candidates.length === 0) return null

  //createdAt is stamped by AuditTrailListener on insert, so it is present on anything the server
  //returned; array order is the fallback for a note added client-side and not yet saved.
  candidates.sort((left, right) => {
    const leftAt = left.note.createdAt ? new Date(left.note.createdAt).getTime() : NaN
    const rightAt = right.note.createdAt ? new Date(right.note.createdAt).getTime() : NaN
    if (isNaN(leftAt) || isNaN(rightAt) || leftAt === rightAt) return left.index - right.index
    return leftAt - rightAt
  })

  return { note: candidates[0].note, previous: candidates[0].parsed.previous, applied: candidates[0].parsed.applied }
}

/*
 * The snapshot note is a record of what was replaced, not something to stick to the top of the list -
 * so it asks not to be pinned, and now gets what it asks for.
 *
 * ⚠ This used to read `pinned: true` DELIBERATELY, to exploit a server-side inversion: every note loop
 * in InventoryService called `TogglePinned(!model.Pinned)` on a freshly-built note, so `false` pinned
 * it and unpinned every other pinned note on the vehicle. That inversion is fixed (all nine sites), so
 * the workaround is gone with it - and the two must never be split across commits, because either half
 * alone re-creates the exact behaviour both were written to avoid. Pinned by
 * SHOULD_NOT_unpin_an_existing_note_when_the_carfax_snapshot_note_is_added.
 */
export const buildCarfaxOverwriteNoteModel = (noteText) => ({
  note: noteText,
  status: 'Active',
  isHotNote: false,
  pinned: false
})

/**
 * Whether a model name already carries this sub-model as a whole word, so printing the two together
 * would say it twice.
 *
 * ⚠ This is the client-side counterpart of CarFaxService.RemoveRedundantSeriesValue, and it exists
 * because of a defect the corrected extraction made visible: the reconcile prompt read
 * "2024 Ferrari 296 GTS GTS Convertible". The message builder appends the resolved row's name
 * ("296 GTS") and then the trim ("GTS"). It was invisible while the extraction folded the sub-model
 * into the model and left the trim blank; it affects every make whose catalogue Name carries the
 * sub-model, which is 701 of the 1,816 active rows.
 *
 * ⚠ Whole-word, so "GT" inside "GTS" is not a match - a short trim would otherwise be swallowed by
 * any name that merely contains those letters. Multi-word trims must match as a run, so
 * "GT2 RS" counts against "911 GT2 RS" but not against a name holding only "RS".
 */
export const modelNameCarriesTrim = (modelName, trim) => {
  if (!isMeaningful(modelName) || !isMeaningful(trim)) return false
  const words = key(modelName).split(' ')
  const trimWords = key(trim).split(' ')
  for (let start = 0; start + trimWords.length <= words.length; start++) {
    let matched = true
    for (let offset = 0; offset < trimWords.length; offset++) {
      if (words[start + offset] !== trimWords[offset]) {
        matched = false
        break
      }
    }
    if (matched) return true
  }
  return false
}

/*
 * The learned-mapping suggestion: what this dealership has previously made of the same CARFAX
 * description. Returns null when there is nothing to suggest, which is the common case.
 *
 * ⚠ Resolved by NAME and the vehicle's own year, NOT by the stored applied_model_id. Catalogue rows
 * are year-scoped - a mapping learned on a 2023 812 Competizione holds the 2023 row's id, and
 * applying that to a 2025 car would silently file it under the wrong year. The id is kept on the
 * server side as provenance only.
 *
 * ⚠ Returns null when the name resolves to no catalogue row for that year. A suggestion must always
 * be catalogue-linked: free text is not a learnable target, and a non-catalogue suggested row would
 * trip CarfaxModelReconcile's not-in-catalogue warning on open, which is worded as a consequence of
 * applying and would be describing an action the operator has not taken.
 *
 * ⚠ Matched on the RAW carfax values (sourceModelName / sourceSeries), not the resolved ones. The
 * resolver joins model and series into one name, and the mapping is keyed on the two halves as
 * CARFAX actually sent them.
 */
export const findLearnedMapping = (mappings, foundMake, sourceModelName, sourceSeries, productionYear) => {
  if (!mappings || mappings.length === 0) return null
  if (!isMeaningful(sourceModelName)) return null

  const modelKey = key(sourceModelName)
  const seriesKey = isMeaningful(sourceSeries) ? key(sourceSeries) : ''

  /*
   * ⚠ THE MAKE IS PART OF THE KEY. A CARFAX description is only meaningful within a make - "S" is
   * Mercedes only by context - and the server now returns one suggestion per make rather than one per
   * description, so without this filter `.find` takes whichever make came first.
   *
   * ⚠ It cannot be left to findCatalogueModel below to sort out. That returns null when the other
   * make's applied name is absent from this make's catalogue, which masks the problem incidentally and
   * stops masking it the moment two makes hold a model of the same name - and a null there reads as
   * "no suggestion" rather than "wrong make", so nothing ever surfaces.
   *
   * appliedMakeId is 0 on a mapping whose applied model was deleted; those are matched as before
   * rather than silently dropped, and findCatalogueModel still has the last word.
   */
  const match = mappings.find((x) => key(x.carfaxModel) === modelKey && key(x.carfaxSeries) === seriesKey &&
    (!x.appliedMakeId || !foundMake || x.appliedMakeId === foundMake.id))
  if (!match) return null

  const catalogueModel = findCatalogueModel(foundMake, match.appliedModelName, productionYear)
  if (!catalogueModel) return null

  return {
    model: catalogueModel,
    name: catalogueModel.name,
    trim: isMeaningful(match.appliedTrim) ? squash(match.appliedTrim) : '',
    timesSeen: match.timesSeen,
    timesTotal: match.timesTotal,
    unanimous: !!match.unanimous,
    label: buildLearnedMappingSourceLabel(match)
  }
}

/*
 * The Source column text for the suggested row. It names where the suggestion came from AND how
 * strong the agreement is, because "I cannot tell where a suggested value came from" was raised as
 * its own complaint - a bare "Suggested" would repeat that mistake.
 */
export const buildLearnedMappingSourceLabel = (mapping) => {
  if (!mapping) return ''
  const seen = mapping.timesSeen
  const total = mapping.timesTotal
  return total > seen
    ? 'Previous corrections · ' + seen + ' of ' + total
    : 'Previous corrections · ' + seen
}

/*
 * What to teach the mapping table from this apply, or null when there is nothing to teach.
 *
 * ⚠ Keyed on the RAW carfax halves, matching how the mapping is stored and how findLearnedMapping
 * looks it up. Using the resolved name would key on the joined string and never match.
 *
 * ⚠ Only a CATALOGUE model is recorded. Free text is not a learnable target - a suggestion has to
 * resolve to a real row or the prompt cannot offer it - so an apply that lands on free text teaches
 * nothing and returns null here rather than storing an id of 0.
 *
 * ⚠ Known property, stated rather than hidden: this records what was FILED, not that a human
 * deliberated. An operator who accepts a pre-ticked unanimous suggestion reinforces it. That is the
 * same semantics the historical backfill has - it counted records, not decisions - so the two agree,
 * but it does mean times_seen is a frequency, not a vote count.
 *
 * ⚠ Takes the model the record ENDED UP ON, not the row the operator ticked - and the difference is
 * not academic. Apply with nothing ticked applies the "current" row, whose model is null by design,
 * and applyCarfaxMakeModel then falls back to CARFAX's own resolution - which still lands the record
 * on a catalogue row. Keyed on the ticked row, every one of those corrections taught nothing, which
 * is the common path rather than an edge case: the prompt only pre-ticks where a unanimous mapping
 * already exists, so a description being seen for the FIRST time never had a tick to read.
 */
export const buildCarfaxModelMappingRecord = (carfaxMakeModel, appliedModelId, appliedTrim) => {
  if (!(appliedModelId > 0)) return null

  const source = carfaxMakeModel || {}
  if (!isMeaningful(source.sourceModelName)) return null

  return {
    carfaxModel: squash(source.sourceModelName),
    carfaxSeries: isMeaningful(source.sourceSeries) ? squash(source.sourceSeries) : '',
    appliedModelId: appliedModelId,
    appliedTrim: isMeaningful(appliedTrim) ? squash(appliedTrim) : ''
  }
}
